import * as yup from 'yup'

import { AutobookConstants } from './AutobookConstants'
import { CustomerDetails } from './CustomerInfo'
import { IBranch } from './IBranch'
import { IBookingEnvelope, IOperation } from './IOperation'

class MovingDetails {
  requestedDay = ''
  branchId = ''
}
class MoveContractRequest {
  customerInformation: CustomerDetails = new CustomerDetails()
  moveDetails: MovingDetails = new MovingDetails()
}

class CreateCustomerPayload {
  id = ''
  moveContractRequest: MoveContractRequest = new MoveContractRequest()
}

class NewMoveModel {
  _id = ''
}

class MovingContract {
  move: NewMoveModel = new NewMoveModel()
}

class MoveMetaData {
  requestedDay = ''
  moveAlerts = ''
  created: Date | null = null
}

class EmailReceipt {
  _id = ''
  template = ''
  rfc822msgid?: string
  sender?: string
  sent?: string
}

const MoveMetaDataSchema = yup.object().shape({
  moveAlerts: yup.string().optional(),
})

class LegacyMoveModel {
  meta: MoveMetaData = new MoveMetaData()
  activities: MoveActivity[] = []
  emails: EmailReceipt[] = []
}

class BookingCourtesyCall {
  sent = false
}

interface AutobookCommunicationRecord {
  sent: boolean
  by: string
  when: Date
}

interface AutoBookCommuncations {
  confirmation?: AutobookCommunicationRecord
  unreachableConfirmation?: AutobookCommunicationRecord
  firstConfirmationCall?: AutobookCommunicationRecord
}

class CustomerResult {
  _id = ''
  firstName = ''
  lastName = ''
  phone = ''
  phoneType = ''
  altPhone = ''
  altPhoneType = ''
  email = ''
  altEmail = ''
  branchId = ''
  altFirstName = ''
  altLastName = ''
  move: LegacyMoveModel = new LegacyMoveModel()
  created?: Date
  branch?: IBranch // virtual
  managedBy?: string
  externalId?: string
  status = ''
  nounce = ''
  referredBy = ''
  isRepeat = false
  previousContractId = ''
  courtesyCall?: BookingCourtesyCall = new BookingCourtesyCall()
  autobookingCommunications?: AutoBookCommuncations
  virtualWalkthrough?: AutobookCommunicationRecord
}
class CustomerSearchResults {
  total = 0
  count = 0
  size = 0
  page = 0
  customers: CustomerResult[] = []
}

class FetchedCustomer {
  currentCustomer: CustomerResult = new CustomerResult()
}

class MoveDetails {
  currentMove: LegacyMoveModel = new LegacyMoveModel()
  handleActivityCreation = () => {
    /* ignore */
  }
}

class ActivitiesProps {
  activities: MoveActivity[] = []
  createActivity = () => {
    /* ignore */
  }
}

class LocationEstimate {
  hours = 0
  minutes = 0
  overrideReason = ''
}

class WorktimeInMinutes {
  forWalking = 0
  forFurniture = 0
  forStories = 0
  forBoxes = 0
  forDriving = 0
}
class LocationInputsUsed {
  twoMoverTrips = 0
  oneMoverTrips = 0
  boxes = 0
  twoMenFurnitureEquivalent = 0
}
class WorkTimeEstimates {
  timeInMinutes: WorktimeInMinutes = new WorktimeInMinutes()
  inputsUsed: LocationInputsUsed = new LocationInputsUsed()
}
class ApiBusinessError extends Error {
  constructor(message: string) {
    super(message)
  }
}

class ActivityLocation {
  _id?: string
  street = ''
  unit = ''
  city = ''
  state = ''
  zip = ''
  notes = ''
  distanceToDoor = '0'
  locationType = 'House'
  sqft = ''
  bedrooms = ''
  otherDetails = ''
  additionalRooms = ''
  dimensions = ''
  hasElevator: string | boolean = 'no'
  employees = '0'
  floor = ''
  storageType = 'Outdoor'
  stories = ''
  zone = 'O'
  driveTime = '0'
  driveDistance = '0'
  isPacking = false
  packingEstTime = null
  packingEstCost = null
  packingAmounts = ''
  tripFee = 0
  numBoxes = 0
  useFee = false
  latitude = 0
  longitude = 0
  normalizedStreetAddress = ''
  typeOfWOrk = ''
  currentEstimate: LocationEstimate = new LocationEstimate()
  driveTimeFetchStatus = ''
  twoMenTruckTrips = 0
  oneManTruckTrips = 0
  walkTimeOverride = 0
  stairTimeOverride = 0
  elevatorTimeOverride = 0
  storiesTimeOverride = 0
  defaultTwoMenTruckTrips = 0
  defaultOneManTruckTrips = 0
  defaultBoxes = 0
  worktimeEstimations: WorkTimeEstimates[] = []
  walkingTime = 0
  stairsTime = 0

  currentTwoMenTruckTrips = 0
  currentOneManTruckTrips = 0
  currentBoxes = 0
  parkingType = ''

  overrideEstimateHours = 0
  overrideEstimateMins = 0
  overrideReason = ''
  hasExcessInventory = false
  isProblem = false
}

class ActivityOperation {
  _id = ''
  truck = ''
  moverCount = 0
  date = ''
  timezone = ''
  duration = 0
  earlyStart = false
  earlyStartNotes = ''
  requiresLift = false
  bookingEnvelope: IBookingEnvelope = {} as IBookingEnvelope
}

class MoveActivity {
  _id = ''
  activityType = 'move'
  toBeMoved = ''
  alerts = ''
  notes = ''
  inventory = ''
  activityDay = ''
  locations: ActivityLocation[] = []
  operations: ActivityOperation[] = []
  hasPrevDayActivity = false
  hasNextDayActivity = false

  numMovers = 2
  estHours = 4
  truckCount = 1
  minTime = 2.5

  state = 'uncommitted' //from all operation truck assignments

  totalTripFees = 0 //from sum of locations
  totalPacking = 0 //sum of locations

  totalHourlyRate = 0 // rate * #movers
  estCost = 0 //time * $ * movers + trip fees
  estCostMin = 0 //minTime * $ * movers + trip fees
  estTotalCost = 0 //estCost + packing cost for all locations
  estTotalCostMin = 0 //estCostMin + packing cost for all locations

  moveRate = 0
  costingStrategy = ''
}

class CustomerSearchCriterion {
  branchId = ''
  searchText = ''
  filter = ''
  page = 1
  pageSize = 15
  sort = 'activity'
  order = 'desc'
}

export interface ICustomerSearchCriterion {
  searchText?: string
  branchId: string
  page?: number
  size?: number
  sort?: string
  order?: 'asc' | 'desc'
}
export interface IActivityRecalendarPayload {
  movingContractId: string
  activityId: string
  date: string
  customer?: CustomerResult
}

export interface IOperationManagementPayload {
  movingContractId: string
  activityId: string
  operation: IOperation
  customer?: CustomerResult
}

export interface IOperationBookingPayload {
  movingContractId: string
  activityId: string
  operation: IOperation
  customer?: CustomerResult
}

export interface ICustomerRebranchPayload {
  movingContractId: string
  branchId: string
}

class LocationCreationPayload {
  movingContractId = ''
  activityId = ''
  location: ActivityLocation = new ActivityLocation()
  move: LegacyMoveModel = new LegacyMoveModel()
}

class LocationOverrideDetails {
  id = ''
  storiesTimeOverride = 0
  elevatorTimeOverride = 0
  stairTimeOverride = 0
  walkTimeOverride = 0
  numBoxes = 0
  oneManTruckTrips = 0
  twoMenTruckTrips = 0
  hrs = 0
  mins = 0
  overrideReason = ''
}

class LocationReestimationPayload {
  contractId = ''
  activityId = ''
  locationOverrides: LocationOverrideDetails[] = []
}

class LocationReorderPayload {
  contractId = ''
  activityId = ''
  locations: ActivityLocation[] = []
}

const CustomerSearchCriterionSchema = yup.object().shape({
  searchText: yup.string().required('This field is required'),
  branchId: yup.string().required('This field is required'),
})

class ActivityEstimationDetails {
  numMovers = 0
  truckCount = 0
  estHours = 0
  minTime = 0
}

class OverrideEstimateDetails {
  locations: ActivityLocation[] = []
}

class MoveMngrActivityCostingPayload {
  movingContractId = ''
  activityId = ''
  inputs: ActivityEstimationDetails = new ActivityEstimationDetails()
}

export interface IAddLocationProps {
  open: boolean
  setOpen: any
  existing?: ActivityLocation
}

export interface ILocationsListingProps {
  activity: MoveActivity
  notifyWithUpdatedLocations: any
}

export interface ILocationListingCatalogueProps {
  items: ActivityLocation[]
  activity: MoveActivity
  setCurrent: any
  globalIndexPosition: number
  dragInProgress: boolean
  canDropForPositionShift: boolean
}

export interface ILocationListingSectionProps {
  items: ActivityLocation[]
  activity: MoveActivity
  setCurrent: any
  current: ActivityLocation
  globalIndexPositon: number
  locationDragEnded: (result: any, provided: any) => void
  locationDragStart: (initial: any, provided: any) => void
  onDragUpdate?: (initial: any, provided: any) => void
  dragInProgress: boolean
  canDropForPositionShift: boolean
}

export interface ILocationListingElementProps {
  item: ActivityLocation
  setAsCurrent: any
  locationIndex: number
  globalIndexPosition: number
  canDropForPositionShift: boolean
}

export interface ILocationViewAugmentationProps {
  item: ActivityLocation
  activity: MoveActivity
}

export interface ILocationListingViewMapProps {
  open: boolean
  setOpen: any
  streetAddress: string
  latitude: number
  longitude: number
}

export interface IMoveMgrActivityEstimationProps {
  open: boolean
  setOpen: any
}

export interface IEstimateOverrideProps extends IMoveMgrActivityEstimationProps {
  autobookConstants: AutobookConstants | null | undefined
}

const ActivityEstimationDetailsSchema = yup.object().shape({
  numMovers: yup
    .number()
    .required('Number of movers is required')
    .positive('Mover count must be a positive whole number')
    .integer('Mover count must be a positive whole number'),
  truckCount: yup
    .number()
    .required('Truck count is required')
    .positive('Truck count must be a positive whole number')
    .integer('Truck count must be a whole number'),
  estHours: yup.number().required('Hours is required').positive('Hours must be a positive number'),
  minTime: yup.number().required('Minimum Hours is required').positive('Minimum hours must be a positive number'),
})

const OverrideEstimationDetailsSchema = yup.object().shape({})

export {
  ActivitiesProps,
  ActivityEstimationDetails,
  ActivityEstimationDetailsSchema,
  ActivityLocation,
  ActivityOperation,
  ApiBusinessError,
  BookingCourtesyCall,
  CreateCustomerPayload,
  CustomerResult,
  CustomerSearchCriterion,
  CustomerSearchCriterionSchema,
  CustomerSearchResults,
  EmailReceipt,
  FetchedCustomer,
  LegacyMoveModel,
  LocationCreationPayload,
  LocationOverrideDetails,
  LocationReestimationPayload,
  LocationReorderPayload,
  MoveActivity,
  MoveContractRequest,
  MoveDetails,
  MoveMetaDataSchema,
  MoveMngrActivityCostingPayload,
  MovingContract,
  MovingDetails,
  OverrideEstimateDetails,
  OverrideEstimationDetailsSchema,
}
export interface ICommunicationRecord {
  sent: boolean
  by: string
  when: string | Date
}

export interface IAutobookingCommunication {
  confirmation: ICommunicationRecord
  unreachableConfirmation: ICommunicationRecord
  firstConfirmationCall: ICommunicationRecord
}
